import { memo, type RefObject } from "react";
import styled from "styled-components";

import { SaveIcon } from "../../icons/glyphs";
import { ContentSwitcher, type ContentSwitcherOption } from "../ContentSwitcher";
import { IconButton, TooltipIconButton } from "../IconButton";
import { Spinner } from "../Spinner";
import { Text, defaultTextVariants } from "../Text";
import {
  AnnotationCameraLabel,
  AnnotationPanelHeader,
  AnnotationPanelHeaderActions,
  AnnotationPanelHeaderTitle,
  CapturedFramePanelContent,
  CapturedFrameSectionRoot,
} from "./AnnotationPanelLayout";
import { CapturedFrameList } from "./CapturedFrameList";
import {
  annotationStudioText,
  capturedFrameFilterEmptyMessage,
  capturedFrameFilterSummary,
} from "./messages";
import type {
  AnnotationFrameDraft,
  AnnotationFrameSummary,
  AnnotationSequence,
  AnnotationStream,
  AnnotationStudioLocale,
  AnnotationStudioViewMode,
  AnnotationTarget,
} from "./types";

export type CapturedFrameScope = "all-camera" | "selected-episode";

export interface CapturedFramePanelProps {
  readonly locale: AnnotationStudioLocale;
  readonly viewMode: AnnotationStudioViewMode;
  readonly streamLabel: string;
  readonly streamFieldLabel: string;
  readonly sequences: readonly AnnotationSequence[];
  readonly streams: readonly AnnotationStream[];
  readonly targets: readonly AnnotationTarget[];
  readonly capturedFrames: readonly (AnnotationFrameDraft | AnnotationFrameSummary)[];
  readonly visibleFrames: readonly (AnnotationFrameDraft | AnnotationFrameSummary)[];
  readonly displayedFrameCount: number;
  readonly frameScope: CapturedFrameScope;
  readonly onFrameScopeChange: (scope: CapturedFrameScope) => void;
  readonly selectedSequenceId: string | null;
  readonly selectedSequenceLabel: string;
  readonly activeFrameId: string | null;
  readonly selectedFrameId: string | null;
  readonly previewFrameId: string | null;
  readonly previewFrame?: AnnotationFrameDraft | AnnotationFrameSummary;
  readonly previewOutsideFilter: boolean;
  readonly materializingFrameId?: string | null;
  readonly materializationFailedFrameId?: string | null;
  readonly savedAnnotationFrameIds: ReadonlySet<string>;
  readonly dirtyFrameIds: ReadonlySet<string>;
  readonly saveConflictFrameIds: ReadonlySet<string>;
  readonly validationIssueFrameIds: ReadonlySet<string>;
  readonly page: number;
  readonly pageCount: number;
  readonly listViewportRef: RefObject<HTMLDivElement | null>;
  readonly listRef: RefObject<HTMLUListElement | null>;
  readonly authoring: boolean;
  readonly hasUnsavedCameraChanges: boolean;
  readonly cameraSavePending: boolean;
  readonly cameraSaveDisabled: boolean;
  readonly authoringDisabledReasonId?: string;
  readonly removeAvailable: boolean;
  readonly removeDisabled: boolean;
  readonly onSaveCameraAnnotations: () => void;
  readonly onSelectFrame: (frameId: string) => void;
  readonly onTogglePreview: (frameId: string) => void;
  readonly onRequestRemove: (frameId: string) => void;
  readonly onPreviousPage: () => void;
  readonly onNextPage: () => void;
}

const Empty = styled(Text)`
  padding: 0.65rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surface};
  color: ${({ theme }) => theme.color.text.subtle};
`;

const FrameFilter = styled.div`
  display: grid;
  min-width: 0;
  gap: 0.2rem;
`;

const FrameScopeSwitcher = styled(ContentSwitcher)`
  & button {
    overflow: hidden;
  }

  & button > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FrameFilterSummary = styled(Text)<{ $visible: boolean }>`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.color.text.subtle};
  font-variant-numeric: tabular-nums;
  text-overflow: ellipsis;
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  white-space: nowrap;
`;

const EmptyActions = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

function CapturedFramePanelComponent({
  locale,
  viewMode,
  streamLabel,
  streamFieldLabel,
  sequences,
  streams,
  targets,
  capturedFrames,
  visibleFrames,
  displayedFrameCount,
  frameScope,
  onFrameScopeChange,
  selectedSequenceId,
  selectedSequenceLabel,
  activeFrameId,
  selectedFrameId,
  previewFrameId,
  previewFrame,
  previewOutsideFilter,
  materializingFrameId = null,
  materializationFailedFrameId = null,
  savedAnnotationFrameIds,
  dirtyFrameIds,
  saveConflictFrameIds,
  validationIssueFrameIds,
  page,
  pageCount,
  listViewportRef,
  listRef,
  authoring,
  hasUnsavedCameraChanges,
  cameraSavePending,
  cameraSaveDisabled,
  authoringDisabledReasonId,
  removeAvailable,
  removeDisabled,
  onSaveCameraAnnotations,
  onSelectFrame,
  onTogglePreview,
  onRequestRemove,
  onPreviousPage,
  onNextPage,
}: CapturedFramePanelProps) {
  const selectedEpisodeFilterActive = frameScope === "selected-episode";
  const cameraSaveLabel = annotationStudioText(
    cameraSavePending ? "savingCameraAnnotations" : "saveCameraAnnotations",
    locale,
  );
  const cameraSaveActionLabel = annotationStudioText(
    cameraSavePending ? "savingCameraAnnotationsAction" : "saveCameraAnnotationsAction",
    locale,
  );
  const scopedCameraSaveLabel = `${cameraSaveLabel}: ${streamLabel}`;
  const hiddenFrameCount = Math.max(0, capturedFrames.length - displayedFrameCount);
  const filteredEmpty =
    selectedEpisodeFilterActive && displayedFrameCount === 0 && capturedFrames.length > 0;
  const selectedEpisodeScopeLabel =
    selectedSequenceId === null
      ? annotationStudioText("selectedEpisodeFrames", locale)
      : selectedSequenceLabel;
  const frameScopeOptions: readonly ContentSwitcherOption[] = [
    {
      value: "selected-episode",
      label: selectedEpisodeScopeLabel,
      tooltip:
        selectedSequenceId === null
          ? undefined
          : `${annotationStudioText("selectedEpisodeFrames", locale)}: ${selectedEpisodeScopeLabel}`,
      disabled: selectedSequenceId === null,
    },
    {
      value: "all-camera",
      label: annotationStudioText("allEpisodes", locale),
    },
  ];
  const handleFrameScopeChange = (value: string) => {
    if (value === "all-camera" || value === "selected-episode") {
      onFrameScopeChange(value);
    }
  };

  return (
    <CapturedFrameSectionRoot
      $filterActive={selectedEpisodeFilterActive}
      data-captured-frame-section="fixed"
    >
      <CapturedFramePanelContent
        $filterActive={selectedEpisodeFilterActive}
        data-captured-frame-panel-content=""
      >
        <AnnotationPanelHeader>
          <AnnotationPanelHeaderTitle>
            <Text variant={defaultTextVariants["caption-bold"]}>
              {annotationStudioText("capturedFrame", locale)}
            </Text>
            <AnnotationCameraLabel
              label={streamLabel}
              ariaLabel={`${streamFieldLabel}: ${streamLabel}`}
            />
          </AnnotationPanelHeaderTitle>
          {authoring ? (
            <AnnotationPanelHeaderActions>
              <TooltipIconButton
                data-camera-save-action=""
                data-camera-save-state={
                  cameraSavePending ? "pending" : hasUnsavedCameraChanges ? "dirty" : "clean"
                }
                size="sm"
                variant="surface"
                text={cameraSaveActionLabel}
                aria-label={scopedCameraSaveLabel}
                aria-keyshortcuts="Control+S Meta+S"
                aria-describedby={cameraSaveDisabled ? authoringDisabledReasonId : undefined}
                tooltip={{ content: scopedCameraSaveLabel, placement: "top" }}
                disabledTooltip={
                  cameraSavePending || hasUnsavedCameraChanges
                    ? scopedCameraSaveLabel
                    : `${annotationStudioText("noCameraAnnotationChanges", locale)}: ${streamLabel}`
                }
                disabled={cameraSaveDisabled}
                onClick={onSaveCameraAnnotations}
              >
                {cameraSavePending ? (
                  <Spinner label={scopedCameraSaveLabel} size="sm" />
                ) : (
                  <SaveIcon aria-hidden="true" />
                )}
              </TooltipIconButton>
            </AnnotationPanelHeaderActions>
          ) : null}
        </AnnotationPanelHeader>

        <FrameFilter data-frame-filter={selectedEpisodeFilterActive ? "active" : "all"}>
          <FrameScopeSwitcher
            options={frameScopeOptions}
            value={frameScope}
            onValueChange={handleFrameScopeChange}
            label={annotationStudioText("frameDisplayScope", locale)}
            size="sm"
            width={{ type: "expanded" }}
          />
          <FrameFilterSummary
            $visible={selectedEpisodeFilterActive}
            variant={defaultTextVariants["caption-normal"]}
            data-frame-filter-summary-slot=""
            data-frame-filter-summary={selectedEpisodeFilterActive ? "" : undefined}
            aria-hidden={!selectedEpisodeFilterActive}
          >
            {selectedEpisodeFilterActive
              ? capturedFrameFilterSummary(
                  locale,
                  selectedSequenceLabel,
                  displayedFrameCount,
                  capturedFrames.length,
                )
              : "\u00a0"}
          </FrameFilterSummary>
        </FrameFilter>

        <CapturedFrameList
          locale={locale}
          viewMode={viewMode}
          sequences={sequences}
          streams={streams}
          targets={targets}
          displayedFrameCount={displayedFrameCount}
          visibleFrames={visibleFrames}
          previewFrame={previewFrame}
          previewOutsideFilter={previewOutsideFilter}
          filterActive={selectedEpisodeFilterActive}
          selectedSequenceId={selectedSequenceId}
          activeFrameId={activeFrameId}
          selectedFrameId={selectedFrameId}
          previewFrameId={previewFrameId}
          materializingFrameId={materializingFrameId}
          materializationFailedFrameId={materializationFailedFrameId}
          savedAnnotationFrameIds={savedAnnotationFrameIds}
          dirtyFrameIds={dirtyFrameIds}
          saveConflictFrameIds={saveConflictFrameIds}
          validationIssueFrameIds={validationIssueFrameIds}
          page={page}
          pageCount={pageCount}
          listViewportRef={listViewportRef}
          listRef={listRef}
          authoring={authoring}
          removeAvailable={removeAvailable}
          removeDisabled={removeDisabled}
          onSelectFrame={onSelectFrame}
          onTogglePreview={onTogglePreview}
          onRequestRemove={onRequestRemove}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
        />

        {filteredEmpty ? (
          <Empty variant={defaultTextVariants["caption-normal"]}>
            {capturedFrameFilterEmptyMessage(locale, selectedSequenceLabel, hiddenFrameCount)}
            <EmptyActions>
              <IconButton
                text={annotationStudioText("showAllFrames", locale)}
                size="sm"
                variant="surface"
                onClick={() => onFrameScopeChange("all-camera")}
              />
            </EmptyActions>
          </Empty>
        ) : capturedFrames.length === 0 ? (
          <Empty variant={defaultTextVariants["caption-normal"]}>
            {annotationStudioText(
              viewMode === "annotate" ? "noCapturedFramesForContext" : "emptyCapturedFrames",
              locale,
            )}
          </Empty>
        ) : null}
      </CapturedFramePanelContent>
    </CapturedFrameSectionRoot>
  );
}

export const CapturedFramePanel = memo(CapturedFramePanelComponent);
